import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";
import { HeroVideo } from "../homeData";
import { Battery, Cpu, Wind, Zap, ChevronLeft, ChevronRight, X as XIcon, Instagram, Feather } from "lucide-react";

const JETSKI_HERO = "/assets/jetski-hero.webp";

const resolveBlogCopy = (post, t, getContent) => {
  const fallback = t.blogPosts?.[post.slug] || {};
  return {
    ...post,
    title: getContent(`blog.${post.slug}.title`, "text", fallback.title || post.title || ""),
    excerpt: getContent(`blog.${post.slug}.excerpt`, "text", fallback.excerpt || post.excerpt || ""),
    body: getContent(`blog.${post.slug}.body`, "text", fallback.body || post.body || ""),
  };
};

export default function Homepage() {
  const { t, formatPrice, country, getContent } = useApp();
  const [featured, setFeatured] = useState([]);
  const [blog, setBlog] = useState([]);
  const [igPosts, setIgPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then((r) => setFeatured(r.data));
    axios.get(`${API}/blog`).then((r) => setBlog(r.data));
    axios.get(`${API}/instagram/feed`).then((r) => setIgPosts((r.data.posts || []).slice(0, 10))).catch(() => {});
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="bg-white" data-testid="home-final">
      {/* Section 1 — Hero */}
      <section className="relative h-screen overflow-hidden">
        <HeroVideo overlay="linear-gradient(180deg, rgba(15,23,42,0.35) 0%, rgba(15,23,42,0.1) 50%, rgba(255,255,255,0.0) 100%)" />
        <div className="absolute inset-0 flex flex-col justify-end pb-32">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 fade-up text-white">
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] max-w-5xl">{t.hero.title}</h1>
            <p className="font-body text-lg md:text-2xl text-white/95 max-w-3xl mt-8 leading-relaxed font-light">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => scrollTo("home-products")} className="btn-primary" data-testid="hero-cta-shop">{t.hero.cta1}</button>
              <button onClick={() => scrollTo("home-book")} className="btn-ghost !text-white !border-white" data-testid="hero-cta-book">{t.hero.cta2}</button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-hint text-white/80 text-xs font-ui tracking-[0.3em] uppercase">{t.scroll}</div>
      </section>

      {/* Section 2 — USPs */}
      <UspBento t={t} />

      {/* Message 1: Premium Products */}
      <PremiumProductShowcase featured={featured} t={t} formatPrice={formatPrice} country={country} />

      {/* Message 2: Experience / Demo */}
      <LearnVideoSection t={t} />

      {/* Message 3: Future Vision / Ultra AI */}
      <FutureVisionSection t={t} />

      {/* Section 5 — Instagram */}
      <InstagramAutoplaySection posts={igPosts} t={t} />

      {/* Section 6 — Journal */}
      <BlogCarousel blog={blog} t={t} getContent={getContent} />
    </main>
  );
}

function UspBento({ t }) {
  return (
    <section className="py-28 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="section-usps">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        <div className="md:col-span-7 rounded-2xl overflow-hidden p-10 md:p-14 min-h-[340px] flex flex-col justify-end relative text-white" data-testid="usp-weight">
          <img
            src="https://staging.sharkxelectric.com/api/uploads/8422f2cc0d1149d19f22a8a09a1fd5c1.webp"
            alt="SharkX foil board"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/45 to-cyan-500/15" />
          <div className="relative z-10 max-w-md">
            <Feather size={28} className="drop-shadow" />
            <div className="font-serif-display text-7xl md:text-9xl leading-none mt-3 drop-shadow-lg">{t.usps.f1t}</div>
            <div className="mt-3 max-w-md text-white/95 drop-shadow">{t.usps.f1d}</div>
          </div>
        </div>
        <div className="md:col-span-5 rounded-2xl overflow-hidden p-10 min-h-[340px] flex flex-col justify-end relative text-white" data-testid="usp-plug">
          <img src="https://staging.sharkxelectric.com/api/uploads/c3dad0804d0c492f917aecff92e106b8.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 to-slate-900/20" />
          <div className="relative">
            <Battery size={28} />
            <div className="font-serif-display text-5xl mt-3">{t.usps.f2t}</div>
            <div className="mt-2 text-sm opacity-90">{t.usps.f2d}</div>
          </div>
        </div>
        <div className="md:col-span-5 rounded-2xl overflow-hidden p-10 min-h-[260px] flex flex-col justify-end relative text-white" data-testid="usp-mast">
          <img src="https://staging.sharkxelectric.com/api/uploads/239dfcb3d52e4753bfdf8e71891c7b6b.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 to-slate-900/20" />
          <div className="relative">
            <Cpu size={28} />
            <div className="font-serif-display text-5xl mt-3">{t.usps.f3t}</div>
            <div className="mt-2 text-sm opacity-90">{t.usps.f3d}</div>
          </div>
        </div>
        <div className="md:col-span-7 rounded-2xl overflow-hidden p-10 md:p-14 min-h-[260px] flex flex-col justify-end relative text-white" data-testid="usp-endless">
          <img src="https://staging.sharkxelectric.com/api/uploads/809533a2a15e40a4accda86cf267c1ab.webp" alt="SharkX JetSket" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/45 to-cyan-500/15" />
          <Wind size={28} className="relative z-10" />
          <div className="font-serif-display text-5xl md:text-6xl mt-3 relative z-10">{t.usps.endlessTitle}</div>
          <div className="mt-3 max-w-lg opacity-95 relative z-10">{t.usps.endlessBody}</div>
        </div>
      </div>
    </section>
  );
}

function PremiumProductShowcase({ featured, t, formatPrice, country }) {
  // Only current premium products
  const byCat = { efoils: null, jetboards: null };
  featured.forEach((p) => { if (p.category in byCat && !byCat[p.category]) byCat[p.category] = p; });
  const cards = [
    byCat.efoils ? { p: byCat.efoils, title: "SharkX E-Foils", href: "/products/efoils" } : null,
    byCat.jetboards ? { p: byCat.jetboards, title: "SharkX E-Jetboards", href: "/products/jetboards" } : null,
  ].filter(Boolean);

  return (
    <section id="home-products" className="py-24 px-6 md:px-12 max-w-[1440px] mx-auto scroll-mt-24" data-testid="section-showcase">
      <div className="mb-14">
        <div className="overline mb-3 text-sky-600">{t.home.premiumOverline}</div>
        <h2 className="font-serif-display text-5xl md:text-6xl text-slate-900">{t.shop.title}</h2>
        <p className="text-slate-600 mt-4 text-lg max-w-2xl">{t.home.premiumBody}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {cards.map(({ p, title, href }) => (
          <ProductCard key={p.id} product={p} title={title} href={href} t={t} formatPrice={formatPrice} country={country} />
        ))}
      </div>
    </section>
  );
}

function ProductCard({ product, title, href, t, formatPrice, country }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [hovering, setHovering] = useState(false);
  const cardRef = useRef(null);
  // Use card_images if set by admin, otherwise fall back to images
  const imgs = (product.card_images && product.card_images.length > 0) ? product.card_images : (product.images || []);

  // Desktop: immediate switch to img[1] on hover, then 2s rotation
  // Mobile: 2s rotation while card is centered in viewport (handled by IntersectionObserver below)
  useEffect(() => {
    if (!hovering || imgs.length <= 1) return;
    let idx = 0;
    // Immediate first change
    idx = (idx + 1) % imgs.length;
    setImgIdx(idx);
    const id = setInterval(() => {
      idx = (idx + 1) % imgs.length;
      setImgIdx(idx);
    }, 2000);
    return () => clearInterval(id);
  }, [hovering, imgs.length]);

  // Detect mobile centered (intersection + viewport width)
  useEffect(() => {
    if (window.matchMedia("(min-width: 768px)").matches) return;
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0.7) setHovering(true);
        else setHovering(false);
      });
    }, { threshold: [0.7] });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Link ref={cardRef} to={href}
      onMouseEnter={() => setHovering(true)} onMouseLeave={() => { setHovering(false); setImgIdx(0); }}
      className="group card-clean overflow-hidden block" data-testid={`showcase-${product.category}`}>
      <div className="aspect-[4/5] overflow-hidden bg-slate-50 relative">
        {imgs.map((src, i) => (
          <img key={i} src={src} alt={title} loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: i === imgIdx ? 1 : 0 }} />
        ))}
        {country === "BR" && (
          <div className="absolute top-4 left-4 spec-pill bg-emerald-500/95 text-white border-0 backdrop-blur-sm" data-testid="br-delivery-badge">
            {t.product.brDelivery}
          </div>
        )}
      </div>
      <div className="p-7">
        <div className="font-serif-display text-3xl text-slate-900 group-hover:text-sky-500 transition-colors">{title}</div>
        <div className="text-slate-600 text-sm mt-2">{product.tagline}</div>
        <div className="mt-5 flex items-end justify-between">
          <div>
            {product.base_price > 0 && (<>
              <div className="overline">{t.product.from}</div>
              <div className="font-serif-display text-3xl text-sky-600">{formatPrice(product)}</div>
            </>)}
          </div>
          <div className="btn-primary">{t.product.chooseOptions} →</div>
        </div>
      </div>
    </Link>
  );
}

function LearnVideoSection({ t }) {
  const learnVideoSrc = "https://www.youtube.com/embed/jTBapYOuDeM?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&playlist=jTBapYOuDeM";
  return (
    <section id="home-book" className="relative min-h-[620px] overflow-hidden mt-8 scroll-mt-24" data-testid="section-learn">
      <div className="absolute inset-0">
        <div className="hero-video-wrap">
          <iframe src={learnVideoSrc} title="Learn" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-white/10" />
      </div>
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-24 flex items-center min-h-[620px]">
        <div className="max-w-xl">
          <div className="overline mb-4">{t.learn.overline}</div>
          <h2 className="font-serif-display text-5xl md:text-7xl text-slate-900">{t.learn.title}</h2>
          <p className="text-slate-700 mt-6 text-lg">{t.learn.body}</p>
          <Link to="/book" className="btn-primary mt-10" data-testid="home-learn-cta">{t.learn.cta}</Link>
        </div>
      </div>
    </section>
  );
}

const FUTURE_AI_IMG = "/assets/jetski-hero.webp";

function FutureVisionSection({ t }) {
  return (
    <section className="relative py-32 px-6 md:px-12 overflow-hidden bg-slate-950 text-white" data-testid="section-future">
      <div className="absolute inset-0 opacity-40">
        <img src="https://staging.sharkxelectric.com/api/uploads/9090e3512cab4ff2bef8a045622046e0.webp" alt="SharkX Ultra AI" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
      </div>
      <div className="relative max-w-[1440px] mx-auto flex flex-col items-center text-center">
        <div className="overline mb-6 text-sky-400">{t.home.futureOverline}</div>
        <h2 className="font-serif-display text-6xl md:text-8xl lg:text-9xl leading-[0.9] max-w-5xl mb-10">
          SharkX<br /><span className="text-sky-400">Ultra AI</span>
          </h2>
        <p className="font-body text-xl md:text-2xl text-white/80 max-w-3xl mb-12 leading-relaxed font-light">
          {t.ultra.body}
        </p>
        <Link to="/ultra-ai" className="btn-primary text-lg px-10 py-4" data-testid="future-cta">
          {t.home.futureCta} →
        </Link>
      </div>
    </section>
  );
}

function InstagramAutoplaySection({ posts, t }) {
  const sources = posts.slice(0, 10).map((p) => ({ post_url: p.post_url, thumbnail: p.thumbnail, caption: p.caption }));

  if (sources.length === 0) return null;

  return (
    <section className="py-24 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="section-instagram">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline mb-3">@sharkxelectric</div>
          <h2 className="font-serif-display text-5xl md:text-6xl text-slate-900">{t.home.fromFeed}</h2>
        </div>
        <a href="https://www.instagram.com/sharkxelectric" target="_blank" rel="noreferrer" className="flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] hover:text-sky-500 text-slate-700" data-testid="ig-link">
          <Instagram size={16} /> {t.home.follow}
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ig-grid">
        {sources.map((p, i) => (
          <a key={i} href={p.post_url || 'https://www.instagram.com/sharkxelectric'} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-md bg-slate-100 block relative">
            <img src={p.thumbnail} alt={p.caption || 'Instagram post'} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </section>
  );
}

function BlogCarousel({ blog, t, getContent }) {
  const [idx, setIdx] = useState(0);
  const visible = 3;
  const maxIdx = Math.max(0, blog.length - visible);
  const next = () => setIdx((i) => Math.min(maxIdx, i + 1));
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  const shown = blog.slice(idx, idx + visible);

  return (
    <section className="pb-32 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="section-blog">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="overline mb-3">{t.journal.overline}</div>
          <h2 className="font-serif-display text-5xl md:text-6xl text-slate-900">{t.journal.title}</h2>
        </div>
        {blog.length > visible && (
          <div className="flex gap-2" data-testid="blog-carousel-controls">
            <button onClick={prev} disabled={idx === 0} className="w-10 h-10 rounded-full border border-slate-300 grid place-items-center hover:border-sky-500 hover:text-sky-500 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="blog-prev"><ChevronLeft size={16} /></button>
            <button onClick={next} disabled={idx >= maxIdx} className="w-10 h-10 rounded-full border border-slate-300 grid place-items-center hover:border-sky-500 hover:text-sky-500 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="blog-next"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500">
        {shown.map((b) => {
          const post = resolveBlogCopy(b, t, getContent);
          return (
            <Link to={`/blog/${b.slug}`} key={b.id} className="group card-clean overflow-hidden block" data-testid={`blog-${b.slug}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={b.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="overline mb-2">{b.author}</div>
                <div className="font-serif-display text-2xl md:text-3xl leading-tight group-hover:text-sky-500 transition-colors text-slate-900">{post.title}</div>
                <div className="text-slate-600 mt-3 text-sm">{post.excerpt}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
